import React from 'react';
import { Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { ChangeEvent, useContext, useState } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import EmailTextField from 'shared/lib/components/controls/textFields/emailTextField';
import { useTranslation } from 'react-i18next';
import { regulars } from 'shared/lib/utils/globalUtils';
import { AxiosError } from 'axios';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import { NAME_MAX_LENGTH, SURNAME_MAX_LENGTH } from './signupForm';
import { InviteStatus } from 'app/store/lib/authStore';

export default observer(function InviteLinkForm({
  onFinishClick,
}: {
  onFinishClick: () => void;
}) {
  const { t } = useTranslation();

  const {
    mobxStore: { authStore },
  } = useContext(MobxContext);
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const [isLoading, setIsLoading] = useState(false);
  const signupUserData = authStore.signupUserData;

  const validationSchema = yup.object({
    name: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .matches(
        regulars.onlyKirilic,
        t('errors.validationSchema.invalidSymbols')
      )
      .max(
        NAME_MAX_LENGTH,
        t('errors.validationSchema.nameTooLong', { maxLength: NAME_MAX_LENGTH })
      ),
    surname: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .matches(
        regulars.onlyKirilic,
        t('errors.validationSchema.invalidSymbols')
      )
      .max(
        SURNAME_MAX_LENGTH,
        t('errors.validationSchema.surnameTooLong', {
          maxLength: SURNAME_MAX_LENGTH,
        })
      ),
    email: yup
      .string()
      .trim()
      .required(t('errors.validationSchema.required'))
      .matches(regulars.mosRuEmail, t('errors.validationSchema.email')),
  });

  const [error, setError] = useState<null | 'emailAlreadyExists'>(null);
  const [formikSubmited, setFormikSubmited] = useState(false);
  const formik = useFormik({
    initialValues: {
      name: '',
      surname: '',
      email: '',
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async (inviteData) => {
      try {
        setIsLoading(true);
        await authStore.sendSignupLink(inviteData);
        setIsLoading(false);
      } catch (e) {
        const err = e as AxiosError;

        if (err.response?.status === 401) {
          setError('emailAlreadyExists');
        }

        console.error(e);
        setIsLoading(false);
      }
    },
  });

  const isFormikFieldError = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && !!formik.errors[field];

  const getFormikFieldErrorText = (field: keyof typeof formik.initialValues) =>
    formik.touched[field] && formik.errors[field];

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    authStore.clearError();
    formik.handleChange(e);
  };

  const signupEmailSend = authStore.signupEmailSend;

  return (
    <>
      {signupEmailSend === InviteStatus.OK ||
      signupEmailSend === InviteStatus.EXISTS ? (
        <Typography variant="f14" sx={{ textAlign: 'center', fontWeight: 300 }}>
          <span>
            Заявка будет рассмотрена в&nbsp;ближайшее время. Уведомление
            о&nbsp;результатах будет&nbsp;направлено на&nbsp;указанный вами
            e-mail
          </span>
        </Typography>
      ) : signupEmailSend === InviteStatus.IN_WHITE_LIST ||
        signupEmailSend === InviteStatus.EXISTS_IN_WHITE_LIST ? (
        <Typography variant="f14" sx={{ textAlign: 'center', fontWeight: 300 }}>
          <span>
            Добрый день {signupUserData?.name} {signupUserData?.surname}! Для
            окончания процедуры регистрации перейдите по ссылке, отправленной
            вам на почту {signupUserData?.email}. Спасибо!
          </span>
        </Typography>
      ) : null}

      {signupEmailSend ? null : (
        <form
          id="inviteForm"
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(sp.s60),
          }}
          onSubmit={formik.handleSubmit}
        >
          <CustomTextField
            label={t('login.name')}
            id="name"
            name="name"
            typographyVariantSize="f16"
            labelTypographyVariantSize="f12"
            placeholder={t('login.ivanPlaceholder')}
            wrapperSxProps={{
              width: 1,
            }}
            value={formik.values.name}
            onChange={handleOnChange}
            onBlur={formik.handleBlur}
            error={isFormikFieldError('name')}
            helperText={getFormikFieldErrorText('name')}
            autoFocus={true}
            labelSxProps={{
              paddingLeft: '12px',
              color: theme.palette.custom.customDark,
            }}
            sx={{
              '& .MuiInputBase-root': {
                color: theme.palette.custom.customDark,
              },
            }}
          ></CustomTextField>

          <CustomTextField
            label={t('login.surname')}
            id="surname"
            name="surname"
            typographyVariantSize="f16"
            labelTypographyVariantSize="f12"
            placeholder={t('login.ivanovPlaceholder')}
            wrapperSxProps={{
              width: 1,
            }}
            value={formik.values.surname}
            onChange={handleOnChange}
            onBlur={formik.handleBlur}
            error={isFormikFieldError('surname')}
            helperText={getFormikFieldErrorText('surname')}
            labelSxProps={{
              paddingLeft: '12px',
              color: theme.palette.custom.customDark,
            }}
            sx={{
              '& .MuiInputBase-root': {
                color: theme.palette.custom.customDark,
              },
            }}
          ></CustomTextField>
          <EmailTextField
            label={t('login.workEmail')}
            id="email"
            name="email"
            placeholder={t('login.emailPlaceholder')}
            value={formik.values.email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setError(null);
              handleOnChange(e);
            }}
            onBlur={formik.handleBlur}
            error={isFormikFieldError('email') || !!error}
            helperText={
              getFormikFieldErrorText('email') ||
              (error && t(`errors.${error}`))
            }
            autoFocus={true}
            labelSxProps={{
              paddingLeft: '12px',
              color: theme.palette.custom.customDark,
            }}
            sx={{
              '& .MuiInputBase-root': {
                color: theme.palette.custom.customDark,
              },
            }}
          ></EmailTextField>
        </form>
      )}

      <PrimaryButton
        sx={{
          width: 1,
          borderRadius: '16px',
          marginTop: '14px',
        }}
        form="inviteForm"
        type="submit"
        loading={isLoading}
        onClick={(e) => {
          if (signupEmailSend) {
            e.stopPropagation();
            e.preventDefault();
            onFinishClick();
            return;
          }
          setFormikSubmited(true);
        }}
      >
        <Typography variant="f16">
          {signupEmailSend ? t('common.ok') : t('login.createAccount')}
        </Typography>
      </PrimaryButton>
    </>
  );
});
